import { Box, Typography, Chip, Skeleton, Alert, useTheme, alpha, Divider } from '@mui/material';
import { TrendingUp, TrendingDown, DollarSign, Package } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';
import { FoundryItem } from '../../../api/protosCompiled/buildFoundry/buildFoundry_pb';
import { useItemDetails } from '../../../hooks/useItemDetails';

interface ItemDetailsPanelProps {
  itemId: string;
  isExpanded: boolean;
  item: FoundryItem.AsObject;
}

export const ItemDetailsPanel: React.FC<ItemDetailsPanelProps> = ({ itemId, isExpanded, item }) => {
  const theme = useTheme();
  const { itemDetails, isLoading, isError, error } = useItemDetails({
    itemId,
    enabled: isExpanded,
  });

  if (!isExpanded) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="70%" height={16} sx={{ mt: 0.5 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
        <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
          Failed to load item details: {(error as any)?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const priceSummary = itemDetails?.priceSummary;
  const priceStatistics = priceSummary?.priceStatistics;
  const avgPrice = priceStatistics?.avgPrice;
  const minPrice = priceStatistics?.minPrice;
  const maxPrice = priceStatistics?.maxPrice;
  const priceTrend = priceStatistics?.priceTrend;

  return (
    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
      {/* Item Details */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Package size={16} />
          Item Details
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, wordWrap: 'break-word' }}>
          {item.name}
        </Typography>

        {item.description && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
              lineHeight: 1.4,
              wordBreak: 'break-word',
              maxWidth: '400px', // Adjust this value to control wrap width in pixels
              overflowWrap: 'break-word',
              hyphens: 'auto',
              textAlign: 'justify',
            }}
          >
            {item.description}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Price Information */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DollarSign size={16} />
          Pricing Information per {item.primaryUnit?.unitName}
        </Typography>

        {avgPrice && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Average Price:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {formatCurrency(avgPrice, 'USD')}
            </Typography>
            {priceTrend && (
              <Chip
                icon={
                  priceTrend === 'increasing' ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )
                }
                label={
                  priceTrend === 'increasing'
                    ? 'Rising'
                    : priceTrend === 'decreasing'
                      ? 'Falling'
                      : 'Stable'
                }
                size="small"
                color={
                  priceTrend === 'increasing'
                    ? 'error'
                    : priceTrend === 'decreasing'
                      ? 'success'
                      : 'default'
                }
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        )}

        {priceStatistics && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {avgPrice && (
              <Chip
                label={`Avg: ${formatCurrency(avgPrice, 'USD')}`}
                size="small"
                variant="outlined"
              />
            )}
            {minPrice && (
              <Chip
                label={`Min: ${formatCurrency(minPrice, 'USD')}`}
                size="small"
                variant="outlined"
              />
            )}
            {maxPrice && (
              <Chip
                label={`Max: ${formatCurrency(maxPrice, 'USD')}`}
                size="small"
                variant="outlined"
              />
            )}
            {priceSummary?.totalRecords && (
              <Chip
                label={`${priceSummary.totalRecords} records`}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
          </Box>
        )}
      </Box>

      {/* Item Metadata */}
      {(item.complexityScore || item.qualityGrade || item.validationScore) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Package size={16} />
              Item Metadata
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {item.complexityScore && (
                <Chip
                  label={`Complexity: ${item.complexityScore.toFixed(1)}`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              )}
              {item.qualityGrade && (
                <Chip
                  label={`Quality: ${item.qualityGrade}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
              {item.validationScore && (
                <Chip
                  label={`Validation: ${item.validationScore.toFixed(1)}`}
                  size="small"
                  variant="outlined"
                  color="success"
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
